import { Suspense, useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { ImageIcon } from "lucide-react";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { updateCityMutation } from "@/app/Cities/Cities/GraphQL/cityMutations";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { useCityDrawer } from "@/app/Cities/Cities/Drawer/useCityDrawer";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { RegionInput } from "@/app/Common/Components/RegionInput";
import { StateInput } from "@/app/Cities/Cities/Components/StateInput";
import { TimezoneInput } from "@/lib/Components/Form/Inputs/TimezoneInput";
import { toFormikValidate } from "@/lib/Zod/zod-formik-adapter";
import { cityValidation } from "@/app/Cities/Cities/Validation/cityValidation";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { generateSlug } from "@/lib/Utils/string";
import { useTranslations } from "use-intl";
import { CityDescriptionCard } from "@/app/Cities/Cities/Components/CityDescriptionCard";
import { CityHeroImageCard } from "@/app/Cities/Cities/Components/CityHeroImageCard";

export default function CityDrawer() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(true);
  }, []);

  function handleClose() {
    setIsOpen(false);
    setTimeout(() => {
      navigate({
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
        to: "/$tenant/$orgId/supplier/{-$supplierId}/admin/cities",
        params: (prev) => prev,
        search: (prev) => prev,
      });
    }, 300);
  }

  return (
    <Drawer open={isOpen} onClose={handleClose}>
      <DrawerOverlay />
      <DrawerContent
        showCloseButton={true}
        onClose={handleClose}
        className="max-w-2xl"
      >
        <div className="flex h-full flex-col overflow-hidden">
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <Spinner />
              </div>
            }
          >
            <CityDrawerContent />
          </Suspense>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function CityDrawerContent() {
  const city = useCityDrawer();
  const { mutateAsync } = useGqlMutation(updateCityMutation);
  const t = useTranslations("city");

  return (
    <>
      <DrawerHeader className="px-6 py-5">
        <div className="flex items-center gap-4">
          {city.heroImage?.url ? (
            <img
              src={city.heroImage?.url}
              alt={city.name}
              className="h-14 w-14 rounded-lg object-cover ring-1 ring-gray-200"
            />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-gray-100 ring-1 ring-gray-200">
              <ImageIcon className="h-6 w-6 text-gray-400" />
            </div>
          )}
          <div>
            <DrawerTitle>{city.name}</DrawerTitle>
            <DrawerDescription>
              {city.state ? t(`state.${city.state}`) : null} ·{" "}
              {t(`region.${city.region}`)}
            </DrawerDescription>
          </div>
        </div>
      </DrawerHeader>

      <DrawerBody className="space-y-6">
        <RecordScreenFormCard
          title="City Details"
          initialValues={{
            name: city.name,
            slug: city.slug,
            region: city.region,
            state: city.state,
            timezone: city.timezone,
          }}
          validate={toFormikValidate(cityValidation)}
          onSubmit={async (values) => {
            await mutateAsync({
              id: city.id,
              input: values,
            });
          }}
        >
          <CityFormFields />
        </RecordScreenFormCard>

        <CityDescriptionCard cityId={city.id} description={city.description} />

        <CityHeroImageCard cityId={city.id} heroImage={city.heroImage} />
      </DrawerBody>
    </>
  );
}

function CityFormFields() {
  const { values, setFieldValue } = useFormState();

  return (
    <>
      <TextInput
        name="name"
        label="Name"
        className="col-span-6"
        onChange={(val) => {
          if (val) {
            const slug = generateSlug(val);
            setFieldValue("slug", slug);
          }
        }}
      />
      <TextInput
        name="slug"
        label="Slug"
        className="col-span-6"
        description="Lowercase and dashes only"
      />
      <RegionInput
        name="region"
        label="Region"
        className="col-span-6"
        onChange={async () => {
          await setFieldValue("state", null);
        }}
      />
      <StateInput
        name="state"
        label="State"
        region={values.region}
        className="col-span-6"
      />
      <TimezoneInput
        label="Timezone"
        name="timezone"
        className="col-span-full"
      />
    </>
  );
}
